import { useEffect, useState, useRef } from 'react'
import { Container, Grid } from '@material-ui/core'
import { ChatbotConfigurationGeneralView, ChatbotCustomizationView } from '../../views'
import { ChatbotService } from '../../../services';
import { useTranslation } from 'react-i18next';
import { ModalUtil, SimpleLoading } from '../../pures';
import moment from 'moment';
import { TabPure } from '../../pures'
import { useGlobalState } from '../../../GlobalState'

const ChatbotConfigurationGeneralAction = () => {
  const { t } = useTranslation()
  const { t: transl, i18n } = useTranslation('chatbot', { keyPrefix: 'configuration' })
  const modalUtil = new ModalUtil();
  const { botConfig } = useGlobalState()

  const url = process.env.REACT_APP_ETICKET_API;
  const chatbotService = new ChatbotService(url)

  const [loading, setLoading] = useState(true);
  const [initialData, setInitialData] = useState();
  const [assistantTones, setAssistantTones] = useState([]);
  const [assistantTonesRaw, setAssistantTonesRaw] = useState([]);
 
  const [profileImage, setProfileImage] = useState(null)
  const [backgroundImage, setBackgroundImage] = useState(null)

  const [tabSelected, setTabSelected] = useState('generalData')

  const tabs = [
    {
      route: 'generalData',
      title: transl('generalData.title'),
      key: 'generalData.title',
      selected: (tabSelected === 'generalData') ? true : false
    }, {
      route: 'customization',
      title: transl('customization.title'),
      key: 'customization.title',
      selected: (tabSelected === 'customization') ? true : false
    }
  ]

  const handleImageChange = async (newImage, type) => {
    try {
      modalUtil.showModal('loading', t('modal.pure.loading.message'), t('modal.pure.loading.title'))
      const response = await chatbotService.uploadImage(botConfig?.id, newImage, type)

      if (type === 'profile') {
        setProfileImage(newImage)
      } else {
        setBackgroundImage(newImage)
      }

      response.imageUrl && modalUtil.showModal('success', null, t('modal.pure.title.success'))
    } catch (error) {
      modalUtil.showModal('error', error.message, t('modal.pure.title.error'))
    }
  }

  const saveData = async (data) => {
    modalUtil.showModal('loading', t('modal.pure.loading.message'), t('modal.pure.loading.title'))

    try {
      let chatbotData = { ...data }
      let auxInitialData = { ...initialData }

      if (tabSelected === 'generalData') {
        chatbotData.allowGeolocation = !!chatbotData.allowGeolocation
        chatbotData.startTime = moment(chatbotData.startTime).format('HH:mm')
        chatbotData.endTime = moment(chatbotData.endTime).format('HH:mm')
        auxInitialData.generalData = data
      }

      if (tabSelected === 'customization') {
        const auxAssistantTone = assistantTonesRaw.find((assistantTone) => data.assistantTone === transl(`customization.assistantTone.comboboxTypes.${assistantTone.name}`) || data.assistantTone === assistantTone.name)
        chatbotData.assistantToneId = auxAssistantTone.id
        chatbotData.additionalInstructions = data.additionalInstructions
        chatbotData.initialData = data.initialMessage
        chatbotData.sendAttachment = data.sendAttachment
        auxInitialData.customization = data
      }
      
      chatbotData.botConfigId = botConfig?.id

      await chatbotService.updateChatbotConfig(chatbotData)
      setInitialData(auxInitialData)
      modalUtil.showModal('success', null, t('modal.pure.title.success'))
    } catch (error) {
      modalUtil.showModal('error', error.message, t('modal.pure.title.error'))
    }
  }

  const getData = async () => {
    try {
      setLoading(true)
      if (botConfig) {
        let data = await chatbotService.getChatbotConfig(botConfig?.id)
        data = {
          generalData: {
            botId: data?.id,
            chatbotName: data?.chatbotName,
            allowGeolocation: data?.allowGeolocation,
            maxTimeSession: data?.maxTimeSession,
            sessionsPerDay: data?.sessionsPerDay,
            startTime: data?.startTime ? moment(data.startTime, 'HH:mm').toDate() : undefined,
            endTime: data?.endTime ? moment(data.endTime, 'HH:mm').toDate() : undefined
          },
          customization: {
            primaryColor: data?.primaryColor,
            assistantTone: data?.assistantTone?.name,
            additionalInstructions: data?.additionalInstructions,
            profileImage: data?.assistantImagePath,
            backgroundImage: data?.assistantBackgroundPath,
            initialMessage: data?.initialMessage,
            sendAttachment: data?.sendAttachment
          }
        }
  
        setInitialData(data)
        setProfileImage(data.customization.profileImage)
        setBackgroundImage(data.customization.backgroundImage)
        setLoading(false)
      }
    } catch (error) {
      modalUtil.showModal('error', error.message, t('modal.pure.title.error'))
    }
  }

  const getAssistantTones = async () => {
    try {
      let data = await chatbotService.getAssistantTones()
      const assistantTonesNames = data.map(assistantTone => {
        const toneName = assistantTone.name
        return transl(`customization.assistantTone.comboboxTypes.${toneName}`)
      })
      setAssistantTones(assistantTonesNames)
      setAssistantTonesRaw(data)
    } catch (error) {
      modalUtil.showModal('error', error.message, t('modal.pure.title.error'))
    }
  }

  useEffect(() => {
    getAssistantTones()
  }, [])

  useEffect(() => {
    const assistantTonesNames = assistantTonesRaw.map(assistantTone => {
      const toneName = assistantTone.name
      return transl(`generalData.assistantTone.comboboxTypes.${toneName}`)
    })

    setAssistantTones(assistantTonesNames)
  }, [i18n.language])

  useEffect(() => {
    getData()
  }, [botConfig])

  const updateTabStatus = (tabSelected) => {
    setTabSelected(tabSelected)
  }

  if (loading) return <div className='loading'>
    <SimpleLoading />
  </div>

  return (
    <Container className='view-container'>
      <Grid container spacing={3}>
        <TabPure tabs={tabs} tabStatus={updateTabStatus} language={i18n.language} transl={transl} />

        <Grid item xs={12}>
          <Container maxWidth={'md'}>
            <Grid container spacing={2}>
              {
                tabSelected === 'generalData' && (
                  <ChatbotConfigurationGeneralView
                    initialData={initialData.generalData}
                    saveData={saveData}
                  />
                )
              }
              {
                tabSelected === 'customization' && (
                  <ChatbotCustomizationView
                    initialData={initialData.customization}
                    saveData={saveData}
                    assistantTones={assistantTones}
                    assistantTonesRaw={assistantTonesRaw}
                    profileImage={profileImage}
                    backgroundImage={backgroundImage}
                    onProfileImageChange={(img) => handleImageChange(img, 'profile')}
                    onBackgroundImageChange={(img) => handleImageChange(img, 'background')}
                  />
                )
              }
            </Grid>
          </Container>
        </Grid>
      </Grid>
    </Container>

  )
}

export default ChatbotConfigurationGeneralAction